<template>
  <div class="profile-container">
    <h1>个人中心</h1>

    <div v-if="loading" class="loading">
      <p>加载中...</p>
      <div class="loading-spinner"></div>
    </div>

    <div v-else-if="error" class="error-message">
      <h3>❌ 用户信息加载失败</h3>
      <p>{{ error }}</p>
      <div class="error-details" v-if="errorDetails">
        <details>
          <summary>错误详情</summary>
          <pre>{{ errorDetails }}</pre>
        </details>
      </div>
      <div class="retry-actions">
        <button @click="retryFetch" class="retry-btn">🔄 重试加载</button>
        <button @click="checkServerStatus" class="check-btn">🔍 检查服务器</button>
      </div>
    </div>

    <div v-else class="profile-content">
      <div class="profile-info">
        <h2>个人信息</h2>
        <div class="info-item">
          <label>用户名:</label>
          <span>{{ user.username || '未设置' }}</span>
        </div>
        <div class="info-item">
          <label>姓名:</label>
          <span>{{ displayName || '未设置' }}</span>
        </div>
        <div class="info-item">
          <label>邮箱:</label>
          <span>{{ user.email || '未设置' }}</span>
        </div>
        <div class="info-item">
          <label>注册时间:</label>
          <span>{{ formatDate(user.created_at) }}</span>
        </div>
        <!--<div class="info-item" v-if="user.id">
          <label>用户ID:</label>
          <span>{{ user.id }}</span>
        </div>-->
      </div>

      <div class="change-password">
        <h2>修改密码</h2>
        <div v-if="passwordError" class="error-message">{{ passwordError }}</div>
        <div v-if="passwordSuccess" class="success-message">{{ passwordMessage }}</div>

        <form @submit.prevent="changePassword">
          <div class="form-group">
            <label for="currentPassword">当前密码</label>
            <input type="password" id="currentPassword" v-model="passwordForm.currentPassword" required
              autocomplete="current-password" />
          </div>

          <div class="form-group">
            <label for="newPassword">新密码</label>
            <input type="password" id="newPassword" v-model="passwordForm.newPassword" required
              autocomplete="new-password" @input="checkPasswordStrength" />
            <div class="password-strength">
              <div class="strength-bar">
                <div class="strength-indicator" :style="{ width: `${passwordStrength}%` }" :class="strengthClass"></div>
              </div>
              <span class="strength-text">{{ strengthText }}</span>
            </div>
          </div>

          <div class="form-group">
            <label for="confirmPassword">确认新密码</label>
            <input type="password" id="confirmPassword" v-model="passwordForm.confirmPassword" required
              autocomplete="new-password" />
          </div>

          <button type="submit" class="btn btn-primary" :disabled="isSubmitting || !isPasswordValid">
            {{ isSubmitting ? '提交中...' : '修改密码' }}
          </button>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'

export default {
  name: 'ProfileView',
  setup() {
    const store = useStore()
    const router = useRouter()

    const user = computed(() => store.state.user || {})
    const loading = ref(true)
    const error = ref(null)
    const errorDetails = ref(null)
    const isSubmitting = ref(false)
    const retryCount = ref(0)
    const maxRetries = 3

    // 密码修改相关
    const passwordForm = ref({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    })

    const passwordError = ref(null)
    const passwordSuccess = ref(false)
    const passwordMessage = ref('')
    const passwordStrength = ref(0)

    // 计算显示名称 - 优先使用yhm字段
    const displayName = computed(() => {
      const currentUser = user.value
      console.log('计算displayName，当前用户:', currentUser)

      if (currentUser.yhm) {
        console.log('使用yhm属性:', currentUser.yhm)
        return currentUser.yhm
      }

      if (currentUser.name && currentUser.name !== currentUser.username) {
        console.log('使用name属性:', currentUser.name)
        return currentUser.name
      }

      console.log('使用username作为显示名称:', currentUser.username)
      return currentUser.username || '未知用户'
    })

    // 密码强度计算
    const strengthClass = computed(() => {
      if (passwordStrength.value < 30) return 'weak'
      if (passwordStrength.value < 70) return 'medium'
      return 'strong'
    })

    const strengthText = computed(() => {
      if (passwordStrength.value < 30) return '弱'
      if (passwordStrength.value < 70) return '中等'
      return '强'
    })

    const isPasswordValid = computed(() => {
      return passwordForm.value.newPassword.length >= 6 &&
        passwordForm.value.newPassword === passwordForm.value.confirmPassword &&
        passwordForm.value.currentPassword.length > 0
    })

    // 检查密码强度
    const checkPasswordStrength = () => {
      const password = passwordForm.value.newPassword
      let strength = 0

      if (password.length >= 8) strength += 25
      if (/[a-z]/.test(password)) strength += 25
      if (/[A-Z]/.test(password)) strength += 25
      if (/[0-9]/.test(password)) strength += 25
      if (/[^A-Za-z0-9]/.test(password)) strength += 25

      passwordStrength.value = Math.min(strength, 100)
    }

    // 格式化日期
    const formatDate = (dateString) => {
      if (!dateString) return '未知'
      try {
        const date = new Date(dateString)
        return date.toLocaleDateString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        })
      } catch (e) {
        console.error('日期格式化失败:', e)
        return '格式错误'
      }
    }

    // 检查服务器状态
    const checkServerStatus = async () => {
      console.log('检查服务器状态...')

      try {
        // 检查服务器是否运行
        const response = await fetch('https://dhszlib.com:3003/data/db.json', {
          method: 'HEAD',
          mode: 'cors'
        })

        if (response.ok) {
          console.log('✅ 服务器运行正常，端口3003可访问')
          alert('服务器运行正常，请重试加载用户信息')
        } else {
          console.log('❌ 服务器响应异常:', response.status)
          alert(`服务器响应异常: ${response.status} ${response.statusText}`)
        }
      } catch (error) {
        console.error('❌ 服务器连接失败:', error)
        alert('服务器连接失败，请确认：\n1. 服务器是否已启动\n2. 端口3003是否正确\n3. 防火墙是否阻止连接')
      }
    }

    // 直接从db.json获取用户信息
    const fetchUserFromDbJson = async (searchField, searchValue) => {
      console.log(`直接从db.json获取用户信息，搜索字段: ${searchField}, 值: ${searchValue}`)

      try {
        // 直接获取db.json文件
        const response = await fetch('https://dhszlib.com:3003/data/db.json')

        if (!response.ok) {
          throw new Error(`获取db.json失败: ${response.status} ${response.statusText}`)
        }

        const data = await response.json()

        if (!data || !data.users || !Array.isArray(data.users)) {
          throw new Error('db.json格式错误或不包含users数组')
        }

        // 根据提供的字段和值查找用户
        const foundUser = data.users.find(u => u[searchField] === searchValue)

        if (foundUser) {
          console.log(`在db.json中找到用户:`, foundUser)
          return foundUser
        } else {
          console.log(`在db.json中未找到用户，搜索条件: ${searchField}=${searchValue}`)
          return null
        }
      } catch (error) {
        console.error('直接获取db.json用户信息失败:', error)
        throw error
      }
    }

    // 加载用户信息
    const loadUserInfo = async () => {
      if (!store.state.isAuthenticated || !user.value) {
        console.log('用户未登录，跳转到登录页')
        router.push('/')
        return
      }

      loading.value = true
      error.value = null
      errorDetails.value = null

      try {
        console.log('开始加载用户信息...')
        console.log('当前用户状态:', user.value)

        // 如果用户信息中已经有yhm和created_at字段，直接使用
        if (user.value.yhm && user.value.created_at) {
          console.log('用户信息已包含yhm和created_at字段，无需额外加载')
          loading.value = false
          return
        }

        // 尝试从db.json获取更完整的用户信息
        console.log('尝试从db.json获取用户信息...')

        let dbUser = null

        // 优先通过邮箱查找
        if (user.value.email) {
          try {
            dbUser = await store.dispatch('findUserByEmail', user.value.email)
          } catch (emailError) {
            console.error('通过邮箱查找用户失败:', emailError)
          }
        }

        // 如果邮箱查找失败，尝试直接从db.json获取
        if (!dbUser) {
          try {
            // 尝试通过邮箱直接查找
            if (user.value.email) {
              dbUser = await fetchUserFromDbJson('email', user.value.email)
            }

            // 如果邮箱查找失败，尝试通过用户名查找
            if (!dbUser && user.value.username) {
              dbUser = await fetchUserFromDbJson('username', user.value.username)
            }

            // 如果用户名查找失败，尝试通过ID查找
            if (!dbUser && user.value.id) {
              dbUser = await fetchUserFromDbJson('id', user.value.id)
            }
          } catch (directFetchError) {
            console.error('直接从db.json获取用户信息失败:', directFetchError)
            throw directFetchError
          }
        }

        if (dbUser) {
          console.log('成功获取db.json用户信息:', dbUser)

          // 合并用户信息
          const mergedUser = {
            ...user.value,
            yhm: dbUser.yhm,
            email: dbUser.email || user.value.email,
            id: dbUser.id || user.value.id,
            created_at: dbUser.created_at || user.value.created_at
          }

          console.log('合并后的用户信息:', mergedUser)
          store.commit('setUser', mergedUser)
        } else {
          console.warn('未找到对应的db.json用户信息')
        }

      } catch (err) {
        console.error('加载用户信息失败:', err)
        error.value = `加载用户信息失败: ${err.message}`
        errorDetails.value = err.stack || err.toString()

        // 如果是网络错误，提供更具体的错误信息
        if (err.message.includes('fetch')) {
          error.value = '无法连接到服务器，请检查服务器是否运行在端口3003'
        } else if (err.message.includes('JSON')) {
          error.value = '服务器返回的数据格式错误，请检查db.json文件'
        }
      } finally {
        loading.value = false
      }
    }

    // 重试加载
    const retryFetch = async () => {
      if (retryCount.value >= maxRetries) {
        error.value = `已重试${maxRetries}次，仍然失败。请检查服务器配置。`
        return
      }

      retryCount.value++
      console.log(`第${retryCount.value}次重试加载用户信息...`)

      // 清除缓存的用户数据，强制重新加载
      store.commit('setDbUsers', null)

      await loadUserInfo()
    }

    // 修改密码
    const changePassword = async () => {
      if (!isPasswordValid.value) {
        passwordError.value = '请检查密码输入是否正确'
        return
      }

      isSubmitting.value = true
      passwordError.value = null
      passwordSuccess.value = false

      try {
        const response = await store.dispatch('changePassword', {
          currentPassword: passwordForm.value.currentPassword,
          newPassword: passwordForm.value.newPassword
        })

        if (response.success) {
          passwordSuccess.value = true
          passwordMessage.value = '密码修改成功'

          // 清空表单
          passwordForm.value = {
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
          }

          // 3秒后清除成功消息
          setTimeout(() => {
            passwordSuccess.value = false
            passwordMessage.value = ''
          }, 3000)
        } else {
          passwordError.value = response.message || '密码修改失败'
        }
      } catch (error) {
        console.error('修改密码失败:', error)
        passwordError.value = '修改密码时发生错误，请稍后重试'
      } finally {
        isSubmitting.value = false
      }
    }

    // 监听用户状态变化
    watch(user, (newUser) => {
      console.log('用户状态变化:', newUser)
      if (newUser && !loading.value && (!newUser.yhm || !newUser.created_at)) {
        console.log('用户信息缺少yhm或created_at字段，重新加载')
        loadUserInfo()
      }
    }, { immediate: true }) // 修改点1：添加immediate: true

    // 组件挂载时加载用户信息
    onMounted(() => {
      console.log('Profile组件挂载，初始化认证状态')
      store.dispatch('initAuth').then(() => {
        console.log('认证状态初始化完成，开始加载用户信息')
        loadUserInfo();
      });
    });

    watch(
      () => store.state.user,
      (newUser) => {
        console.log('用户状态变化:', newUser)
        if (newUser && !loading.value && (!newUser.yhm || !newUser.created_at)) {
          console.log('用户信息缺少yhm或created_at字段，重新加载')
          loadUserInfo()
        }
      },
      { immediate: true }
    );

    return {
      user,
      loading,
      error,
      errorDetails,
      displayName,
      formatDate,
      retryFetch,
      checkServerStatus,

      // 密码修改相关
      passwordForm,
      passwordError,
      passwordSuccess,
      passwordMessage,
      passwordStrength,
      strengthClass,
      strengthText,
      isPasswordValid,
      isSubmitting,
      checkPasswordStrength,
      changePassword
    }
  }
}
</script>

<style scoped>
.profile-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

.loading {
  text-align: center;
  padding: 40px;
}

.loading-spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 20px auto;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.error-message {
  background-color: #fee;
  border: 1px solid #fcc;
  border-radius: 8px;
  padding: 20px;
  margin: 20px 0;
  color: #c33;
}

.error-details {
  margin-top: 15px;
}

.error-details summary {
  cursor: pointer;
  font-weight: bold;
  margin-bottom: 10px;
}

.error-details pre {
  background-color: #f5f5f5;
  padding: 10px;
  border-radius: 4px;
  overflow-x: auto;
  font-size: 12px;
  max-height: 200px;
}

.retry-actions {
  margin-top: 15px;
  display: flex;
  gap: 10px;
}

.retry-btn,
.check-btn {
  background-color: #3498db;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.3s;
}

.retry-btn:hover,
.check-btn:hover {
  background-color: #2980b9;
}

.check-btn {
  background-color: #e67e22;
}

.check-btn:hover {
  background-color: #d35400;
}

.success-message {
  background-color: #efe;
  border: 1px solid #cfc;
  border-radius: 8px;
  padding: 15px;
  margin: 15px 0;
  color: #3c3;
}

.profile-content {
  display: grid;
  gap: 30px;
}

.profile-info,
.change-password {
  background-color: #f9f9f9;
  border-radius: 10px;
  padding: 25px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.profile-info h2,
.change-password h2 {
  margin-top: 0;
  color: #333;
  border-bottom: 2px solid #3498db;
  padding-bottom: 10px;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #eee;
}

.info-item:last-child {
  border-bottom: none;
}

.info-item label {
  font-weight: bold;
  color: #555;
  min-width: 100px;
}

.info-item span {
  color: #333;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: bold;
  color: #555;
}

.form-group input {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 16px;
  transition: border-color 0.3s;
}

.form-group input:focus {
  border-color: #3498db;
  outline: none;
  box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.2);
}

.btn {
  padding: 12px 20px;
  border: none;
  border-radius: 5px;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.3s, transform 0.2s;
}

.btn-primary {
  background-color: #3498db;
  color: white;
}

.btn-primary:hover {
  background-color: #2980b9;
  transform: translateY(-2px);
}

.btn-primary:disabled {
  background-color: #95a5a6;
  cursor: not-allowed;
  transform: none;
}

.password-strength {
  margin-top: 10px;
}

.strength-bar {
  height: 5px;
  background-color: #eee;
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 5px;
}

.strength-indicator {
  height: 100%;
  transition: width 0.3s, background-color 0.3s;
}

.strength-indicator.weak {
  background-color: #e74c3c;
}

.strength-indicator.medium {
  background-color: #f39c12;
}

.strength-indicator.strong {
  background-color: #2ecc71;
}

.strength-text {
  text-align: right;
  font-size: 12px;
  color: #7f8c8d;
}

/* 响应式设计 */
@media (min-width: 768px) {
  .profile-content {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 767px) {
  .profile-container {
    padding: 15px;
  }

  .profile-info,
  .change-password {
    padding: 20px;
  }

  .info-item {
    flex-direction: column;
    align-items: flex-start;
    padding: 10px 0;
  }

  .info-item label {
    margin-bottom: 5px;
  }

  .btn {
    width: 100%;
  }
}
</style>